/* TURN PAGE ANSWERS AT ONCE (owner, 2026-10-10).

   A click on Turn page (or R) turns the page on screen straight away and the person keeps
   working; the engine saves the turned image, reads the page again and reads the values again
   in the background. This module is the renderer's bookkeeping for that, one entry per page
   (document id + page number), and nothing in it touches a document:

   - `sent`: quarter turns in the one request the engine is answering for this page (0: none).
   - `queued`: quarter turns asked for since that request went, net of full circles. Clicks while
     a request runs coalesce here, and one request for the net turn follows the answer; four
     clicks are no turn at all, and nothing more is sent.
   - `shown`: for each page image the screen may still hold, how many quarter turns to draw on top
     of it. The engine's answer names a new image; until that image has loaded the old one stays on
     screen, still drawn turned, so the page never flicks back for a moment.

   A failed request turns the page back on screen: everything not saved is taken off every image. */

export type PageTurnEntry = {
  readonly sent: number;
  readonly queued: number;
  readonly shown: Readonly<Record<string, number>>;
  /**
   * The image every view keeps drawing (turned by `shown`) while more turns of this page are on their
   * way. An answer that is followed by another request names an in-between orientation; swapping it in
   * showed that turn and re-fitted the page, and four quick clicks kept the page moving for 10-15 s
   * (Dustin, 2026-10-10). Only the last answer's image replaces it.
   */
  readonly onScreen?: string;
};

export const pageTurnKey = (documentId: string, pageNumber: number) =>
  JSON.stringify([documentId, pageNumber]);

/** Quarter turns as the page ends up: 0 to 3. */
export const netQuarterTurns = (turns: number) => ((Math.trunc(turns) % 4) + 4) % 4;

const turnEvery = (shown: Readonly<Record<string, number>>, by: number) =>
  Object.fromEntries(
    Object.entries(shown).map(([path, turns]) => [path, netQuarterTurns(turns + by)]),
  );

/**
 * One more quarter turn clockwise of the page whose image on screen is `imagePath`. `send` is the
 * request to start now (quarter turns), or null when one is already running for this page (the
 * turn waits for its answer) or the net turn is none.
 */
export function askPageTurn(
  entry: PageTurnEntry | undefined,
  imagePath: string,
): { entry: PageTurnEntry; send: number | null } {
  const shown = turnEvery(entry?.shown ?? {}, 1);
  shown[imagePath] = netQuarterTurns((entry?.shown[imagePath] ?? 0) + 1);
  const onScreen = entry?.onScreen ?? imagePath;
  if (entry && entry.sent > 0)
    return {
      entry: { sent: entry.sent, queued: netQuarterTurns(entry.queued + 1), shown, onScreen },
      send: null,
    };
  const send = netQuarterTurns((entry?.queued ?? 0) + 1);
  return send === 0
    ? { entry: { sent: 0, queued: 0, shown, onScreen }, send: null }
    : { entry: { sent: send, queued: 0, shown, onScreen }, send };
}

/**
 * The engine answered the running request. Saved: `imagePath` is the image the page names now,
 * already turned by what was sent, so only the turns asked for since are drawn on it, and those go
 * as the next request. Not saved: nothing still waiting is sent and every image is drawn as saved.
 */
export function settlePageTurn(
  entry: PageTurnEntry | undefined,
  outcome: { saved: true; imagePath: string } | { saved: false },
): { entry: PageTurnEntry | undefined; send: number | null } {
  if (!entry) return { entry: undefined, send: null };
  if (!outcome.saved)
    return {
      entry: { sent: 0, queued: 0, shown: turnEvery(entry.shown, -(entry.sent + entry.queued)) },
      send: null,
    };
  const shown = { ...entry.shown, [outcome.imagePath]: entry.queued };
  // Another request follows: the image on screen stays, still drawn at the person's turn.
  return entry.queued > 0
    ? {
        entry: { sent: entry.queued, queued: 0, shown, onScreen: entry.onScreen },
        send: entry.queued,
      }
    : { entry: { sent: 0, queued: 0, shown, onScreen: outcome.imagePath }, send: null };
}

/** Quarter turns to draw over the image at `imagePath` (the one actually on screen). */
export const pageTurnShown = (
  entry: PageTurnEntry | undefined,
  imagePath: string | null | undefined,
) => (entry && imagePath ? (entry.shown[imagePath] ?? 0) : 0);

/** Still waiting for the engine: a request running, or turns asked for after it. */
export const pageTurnBusy = (entry: PageTurnEntry | undefined) =>
  Boolean(entry && (entry.sent > 0 || entry.queued > 0));

/** Whether any page of this document has a turn the engine has not saved yet. */
export const pageTurnPendingFor = (
  entries: ReadonlyMap<string, PageTurnEntry>,
  documentId: string,
) => {
  for (const [key, entry] of entries) {
    if (!pageTurnBusy(entry)) continue;
    try {
      if ((JSON.parse(key) as [string, number])[0] === documentId) return true;
    } catch {
      /* not a page key */
    }
  }
  return false;
};

/**
 * An entry that is done with: nothing waiting, and the image on screen for it (if it is on
 * screen) is the one the page names, so nothing is drawn turned any more.
 */
export const pageTurnSettled = (
  entry: PageTurnEntry,
  onScreenImagePath: string | null | undefined,
  pageImagePath: string | null | undefined,
) => !pageTurnBusy(entry) && (onScreenImagePath == null || onScreenImagePath === pageImagePath);

/**
 * The CSS transform that draws a page `quarterTurns` clockwise about its centre. A sideways page
 * is shrunk to stay inside the box the upright page filled (width and height are the image's).
 */
export function pageTurnTransform(
  quarterTurns: number,
  width?: number,
  height?: number,
): string | undefined {
  const turns = netQuarterTurns(quarterTurns);
  if (turns === 0) return undefined;
  const fit =
    turns % 2 === 1 && width && height && width > 1 && height > 1
      ? Math.min(width, height) / Math.max(width, height)
      : 1;
  return `rotate(${turns * 90}deg)${fit !== 1 ? ` scale(${Number(fit.toFixed(4))})` : ''}`;
}

/** The image a view draws for a page: the one held on screen while more turns are on their way, else the page's own. */
export const pageTurnImage = (entry: PageTurnEntry | undefined, pageImagePath: string) =>
  entry && pageTurnBusy(entry) && entry.onScreen ? entry.onScreen : pageImagePath;
